import { useEffect, useState, useRef } from "react";
import { Reveal } from "./Reveal";
import { TrendingDown, Dumbbell, Trophy, Flame, Activity, ArrowRight, Loader2, CheckCircle2, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

// Real client before/after transformations (faces hidden for privacy)
const T = {
  one: "https://customer-assets-eiarnc6j.emergentagent.net/job_bootcamp-gains/artifacts/1rkw6c5y_White%20Black%20Modern%20Photo%20Collage%20Before%20After%20Youtube%20Thumbnail%20%282%29_Original_Original.jpeg",
  two: "https://customer-assets-eiarnc6j.emergentagent.net/job_bootcamp-gains/artifacts/piv731q5_IMG_7373_Original.jpeg",
  three: "https://customer-assets-eiarnc6j.emergentagent.net/job_bootcamp-gains/artifacts/nf5avnv0_IMG-20170423-WA0005_Original.jpeg",
  four: "https://customer-assets-eiarnc6j.emergentagent.net/job_bootcamp-gains/artifacts/zb20ki7f_IMG_7372_Original.jpeg",
  five: "https://customer-assets-eiarnc6j.emergentagent.net/job_bootcamp-gains/artifacts/y8s6e6m7_IMG_7374_Original.jpeg",
};

const stories = [
  {
    icon: TrendingDown,
    metric: "Fat loss",
    sub: "leaner & tighter",
    tag: "Fat loss",
    name: "Verified client",
    duration: "Real result",
    image: T.one,
  },
  {
    icon: Dumbbell,
    metric: "Toned",
    sub: "core & strength",
    tag: "Body recomp",
    name: "Verified client",
    duration: "Real result",
    image: T.two,
  },
  {
    icon: Trophy,
    metric: "Slimmer",
    sub: "down a dress size",
    tag: "Weight loss",
    name: "Verified client",
    duration: "Real result",
    image: T.three,
  },
  {
    icon: Flame,
    metric: "Transformed",
    sub: "leaner & healthier",
    tag: "Weight loss",
    name: "Verified client",
    duration: "Real result",
    image: T.four,
  },
  {
    icon: Activity,
    metric: "Stronger",
    sub: "confident & lean",
    tag: "Body recomp",
    name: "Verified client",
    duration: "Real result",
    image: T.five,
  },
];

// Combined before/after photo block
const Transformation = ({ id, image }) => (
  <div className="mb-6 border border-[#1c1c1c] overflow-hidden bg-[#111]" data-testid={`beforeafter-${id}`}>
    <img
      src={image}
      alt="Client before and after transformation"
      loading="lazy"
      className="w-full aspect-[16/10] object-contain object-center"
    />
  </div>
);

function ResultsDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", program: "", timeframe: "", metric: "", story: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls =
    "w-full bg-[#111] border border-[#222] text-white placeholder:text-[#555] px-4 py-3 text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/results", form);
      setDone(true);
      toast("Thank you! Your result was submitted.", { description: "Coach Jean Claude will review it soon." });
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDone(false); }}>
      <DialogTrigger asChild>
        <button
          className="font-mono-accent text-xs border border-white/25 text-white px-6 py-4 hover:bg-white hover:text-black transition-colors"
          data-testid="share-results-btn"
        >
          Already a client? Share your results
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0b0b0b] border border-[#222] text-white rounded-none max-w-lg" data-testid="results-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Share your results</DialogTitle>
          <DialogDescription className="text-[#A0A0A0]">
            Tell us how your training went — with your permission, we may feature your story on the site.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-6 flex flex-col items-start gap-3" data-testid="results-success">
            <CheckCircle2 className="text-[#005EEA]" size={36} />
            <p className="text-[#A0A0A0]">
              Thanks {form.name.split(" ")[0]}! Your story is in — Coach Jean Claude will review it and may
              feature it on the site.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 py-2" data-testid="results-form">
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Name" required value={form.name} onChange={set("name")} data-testid="result-name" />
              <input className={inputCls} type="email" placeholder="Email" required value={form.email} onChange={set("email")} data-testid="result-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Program (e.g. Online)" value={form.program} onChange={set("program")} data-testid="result-program" />
              <input className={inputCls} placeholder="Timeframe (e.g. 3 months)" value={form.timeframe} onChange={set("timeframe")} data-testid="result-timeframe" />
            </div>
            <input className={inputCls} placeholder="Headline result (e.g. -12 lbs, first 5k)" value={form.metric} onChange={set("metric")} data-testid="result-metric" />
            <textarea className={`${inputCls} min-h-[110px] resize-none`} placeholder="Tell us about your journey..." required value={form.story} onChange={set("story")} data-testid="result-story" />
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="accent-[#005EEA] w-4 h-4 mt-0.5" data-testid="result-consent" />
              <span className="text-[#A0A0A0] text-xs">I give permission to feature my result (first name only) on the website.</span>
            </label>
            <button type="submit" disabled={loading} className="w-full font-mono-accent text-xs bg-[#005EEA] text-white px-6 py-4 flex items-center justify-center gap-2 hover:bg-[#0047b3] transition-colors disabled:opacity-60" data-testid="result-submit">
              {loading ? <Loader2 size={15} className="animate-spin" /> : "Submit My Result"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SuccessStories({ onConsult }) {
  const [featured, setFeatured] = useState([]);
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get("/results/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-slide]");
    const step = card ? card.offsetWidth + 16 : el.clientWidth;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    const atStart = el.scrollLeft <= 8;
    if (dir > 0 && atEnd) el.scrollTo({ left: 0, behavior: "smooth" });
    else if (dir < 0 && atStart) el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    else el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => scrollByCards(1), 4000);
    return () => clearInterval(id);
  }, [paused, featured.length]);

  return (
    <section id="results" className="bg-[#050505] py-24 md:py-40 border-t border-[#222]" data-testid="success-stories">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <Reveal className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-mono-accent text-[#005EEA] text-xs mb-4">Success Stories</p>
            <h2 className="font-display text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
              Real people.<br />Real results.
            </h2>
          </div>
          <div className="md:text-right">
            <p className="text-[#A0A0A0] max-w-sm mb-5 md:ml-auto">
              Real transformations from clients who put in the work with GFWJC. Your results could be next.
            </p>
            <ResultsDialog />
          </div>
        </Reveal>

        {/* Auto-rotating, swipeable carousel */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
        >
          <div
            ref={trackRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2"
            data-testid="stories-track"
          >
            {stories.map((s, i) => (
              <div
                key={i}
                data-slide
                className="snap-start shrink-0 basis-[86%] sm:basis-[60%] md:basis-[calc(50%-8px)] lg:basis-[calc(33.333%-11px)]"
              >
                <div
                  className="group h-full border border-[#222] bg-[#0b0b0b] p-6 md:p-7 flex flex-col hover:border-[#005EEA]/40 transition-colors"
                  data-testid={`story-${i}`}
                >
                  <Transformation id={i} image={s.image} />
                  <div className="flex items-center justify-between mb-4">
                    <s.icon className="text-[#005EEA]" size={24} />
                    <span className="font-mono-accent text-[9px] text-[#666] border border-[#222] px-2 py-1">{s.tag}</span>
                  </div>
                  <div className="mb-4">
                    <p className="font-display text-white text-3xl md:text-4xl leading-none">{s.metric}</p>
                    <p className="font-mono-accent text-[10px] text-[#005EEA] mt-2">{s.sub}</p>
                  </div>
                  <div className="mt-auto pt-5 border-t border-[#222] flex items-center justify-between">
                    <span className="font-display text-white text-lg leading-none">{s.name}</span>
                    <span className="font-mono-accent text-[9px] text-[#666]">{s.duration}</span>
                  </div>
                </div>
              </div>
            ))}

            {/* Featured client-submitted results */}
            {featured.map((f) => (
              <div
                key={f.id}
                data-slide
                className="snap-start shrink-0 basis-[86%] sm:basis-[60%] md:basis-[calc(50%-8px)] lg:basis-[calc(33.333%-11px)]"
              >
                <div className="group h-full border border-[#222] bg-[#0b0b0b] p-6 md:p-7 flex flex-col hover:border-[#005EEA]/40 transition-colors" data-testid={`featured-result-${f.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <Star className="text-[#005EEA] fill-[#005EEA]" size={22} />
                    <span className="font-mono-accent text-[9px] text-[#666] border border-[#222] px-2 py-1">{f.program || "Client"}</span>
                  </div>
                  {f.metric && (
                    <div className="mb-4">
                      <p className="font-display text-white text-3xl md:text-4xl leading-none">{f.metric}</p>
                    </div>
                  )}
                  <p className="text-[#A0A0A0] text-sm leading-relaxed flex-1">"{f.story}"</p>
                  <div className="mt-6 pt-5 border-t border-[#222] flex items-center justify-between">
                    <span className="font-display text-white text-lg leading-none">{f.name}</span>
                    <span className="font-mono-accent text-[9px] text-[#666]">{f.timeframe}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between mt-6">
            <span className="font-mono-accent text-[10px] text-[#666]">Swipe or use arrows · auto-rotating</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => scrollByCards(-1)}
                className="p-3 border border-[#222] text-white hover:bg-[#005EEA] hover:border-[#005EEA] transition-colors"
                data-testid="stories-prev"
                aria-label="Previous"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => scrollByCards(1)}
                className="p-3 border border-[#222] text-white hover:bg-[#005EEA] hover:border-[#005EEA] transition-colors"
                data-testid="stories-next"
                aria-label="Next"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* CTA band */}
          <Reveal>
            <div className="mt-4 bg-[#005EEA] p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="story-cta">
              <p className="font-display text-white text-4xl md:text-6xl leading-none">You're next.</p>
              <div className="md:max-w-md">
                <p className="text-white/80 text-sm mb-5 leading-relaxed">
                  Your transformation starts with one conversation. Let's build your plan.
                </p>
                <button
                  onClick={onConsult}
                  className="font-mono-accent text-xs bg-black text-white px-8 py-4 flex items-center justify-center gap-2 hover:bg-[#111] transition-colors"
                  data-testid="story-cta-btn"
                >
                  Start Your Journey <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
